import { SearchInput, SearchProps } from "@/components/basic";
import { cn } from "@gettactic/helpers";
import { ScrollArea } from "@mantine/core";
import React, { useMemo, useRef } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList } from "react-window";
import { VirtualizedListRow } from "./VirtualizedListRow";

export interface SearchableListProps
  extends React.PropsWithChildren<Record<never, never>> {
  title?: string;
  teamSearchQuery: string;
  setSearchQuery: SearchProps["setQuery"];
  actionElement: JSX.Element;
  scrollAreaClassName?: string;
  scrollAreaRootClassName?: string;
  offsetScrollbars?: boolean;
  className?: string;
  maxRenderedItems?: number;
  itemSize?: number;
  itemClassName?: string;
  defaultPadding?: string;
}

export const VirtualizedSearchableList = ({
  title,
  teamSearchQuery,
  setSearchQuery,
  actionElement,
  children,
  scrollAreaClassName,
  scrollAreaRootClassName,
  offsetScrollbars,
  className,
  maxRenderedItems = 50,
  itemSize = 92,
  itemClassName,
  defaultPadding = "px-1"
}: SearchableListProps) => {
  const firstItemRef = useRef<HTMLDivElement>(null);

  const childrenArray = useMemo(() => {
    return React.Children.toArray(children);
  }, [children]);

  const shouldVirtualize = childrenArray.length > maxRenderedItems;

  const renderHeader = () => (
    <>
      {title ? (
        <h3 className="py-1 font-bold text-lg text-secondary [.tactic-v2_&]:text-secondary-foreground">
          {title}
        </h3>
      ) : null}
      <div className="flex flex-col justify-between gap-4 sm:gap-16 md:flex-row md:items-center">
        <div className="flex-1">
          <SearchInput
            fullWidth
            className="w-full"
            query={teamSearchQuery}
            setQuery={setSearchQuery}
          />
        </div>
        {actionElement}
      </div>
    </>
  );

  return (
    <div
      className={cn(
        "flex flex-1 flex-col flex-nowrap items-stretch gap-3 overflow-hidden",
        defaultPadding,
        className
      )}
    >
      {renderHeader()}

      {shouldVirtualize ? (
        <div
          className={cn(
            "h-full rounded-lg border border-gray-300 min-h-[300px]",
            scrollAreaClassName
          )}
        >
          <AutoSizer disableWidth={false}>
            {(size: { width: number; height: number }) => (
              <FixedSizeList
                height={size.height}
                width={size.width}
                itemCount={childrenArray.length}
                itemSize={itemSize}
                itemData={{ childrenArray, itemClassName, firstItemRef }}
              >
                {VirtualizedListRow}
              </FixedSizeList>
            )}
          </AutoSizer>
        </div>
      ) : (
        <ScrollArea
          type="auto"
          offsetScrollbars={offsetScrollbars !== false}
          className={cn("h-full", scrollAreaClassName)}
          classNames={{
            root: cn(
              "rounded-lg border border-gray-300",
              scrollAreaRootClassName
            )
          }}
        >
          <ul className={cn("divide-y divide-gray-200", itemClassName)}>
            {children}
          </ul>
        </ScrollArea>
      )}
    </div>
  );
};
